/** A tela de agenda (SPEC-FE-006, PRD E7.1).
 *
 * O funil responde "em que pé está cada negócio". Esta tela responde "o que eu
 * faço hoje" — e é a única do produto organizada pelo tempo, não pelo estado.
 *
 * Três decisões que o desenho carrega:
 *
 * * **Uma consulta para as três visões.** Mês, semana e dia mudam a janela
 *   pedida, e nada mais. Três caminhos de leitura divergiriam na regra de fuso,
 *   que é justamente onde um calendário erra.
 * * **A cor nunca fala sozinha** (DESIGN.md § 8): verde é hoje, âmbar é
 *   atrasado, neutro é o futuro — e cada linha diz o horário por escrito.
 * * **Clicar leva ao trabalho.** A linha abre a mesma ficha do funil, e não uma
 *   cópia dela: o que se faz depois de ver o compromisso é falar com o cliente
 *   e remarcar, e isso já vive lá.
 */

import { Suspense, useState } from "react";

import { PainelDaOportunidade } from "@/features/detalhe/painelSobDemanda";
import { formatarDinheiro } from "@/features/board/formato";
import { Botao } from "@/shared/ui/Botao";
import { LimiteDeErro } from "@/shared/ui/LimiteDeErro";

import {
  DIAS_DA_SEMANA,
  chaveDoDia,
  diasDaSemana,
  gradeDoMes,
  horaDoCompromisso,
  inicioDoDia,
  mesmoDia,
  navegar,
  rotuloDoPeriodo,
  type Visao,
} from "./calendario";
import { useAgenda } from "./useAgenda";

import type { Compromisso } from "./api";

/** Quantas linhas cabem numa célula do mês antes de virar "+N mais".
 *
 * Quatro é o que o modelo mostra, e o número importa: uma célula que cresce
 * com o dia mais cheio estica a grade inteira e transforma o mês numa lista. */
const LINHAS_POR_DIA = 4;

export function Agenda({ aoVoltar }: { aoVoltar: () => void }) {
  const [visao, setVisao] = useState<Visao>("mes");
  const [referencia, setReferencia] = useState(() => inicioDoDia(new Date()));
  const [aberta, setAberta] = useState<{ id: string; titulo: string } | null>(null);

  const consulta = useAgenda(visao, referencia);
  const porDia = new Map((consulta.data?.days ?? []).map((dia) => [dia.date, dia.items]));
  const hoje = inicioDoDia(new Date());

  function abrir(compromisso: Compromisso) {
    setAberta({ id: compromisso.opportunity_id, titulo: compromisso.title });
  }

  /** Do mês para o dia: é o que o "+N mais" faz, e o gesto natural de quem
   * quer ver o dia cheio por inteiro. */
  function verODia(data: Date) {
    setReferencia(inicioDoDia(data));
    setVisao("dia");
  }

  return (
    // Mesma moldura do arquivo de fechadas, que é a outra tela que ocupa a
    // página inteira: a agenda subia sem margem nenhuma, e o título e o
    // calendário encostavam nas duas bordas da janela — a única tela do produto
    // em que isso acontecia.
    <section className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-6 py-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p className="font-texto text-micro uppercase tracking-wide text-texto-suave">Agenda</p>
          {/* O período é o título da tela: é ele que diz onde a pessoa está
              depois de três cliques na seta. */}
          {/* `first-letter:uppercase`, e **não** `capitalize`: o segundo sobe a
              inicial de **toda** palavra, e o rótulo do mês virava "Agosto De
              2026". A preposição em maiúscula não parece defeito de código —
              parece descuido de quem escreveu o produto. */}
          <h2 className="font-display text-display-md tracking-tight text-texto first-letter:uppercase">
            {rotuloDoPeriodo(visao, referencia)}
          </h2>
        </div>

        <div data-guia="agenda-periodo" className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Período anterior"
              onClick={() => {
                setReferencia((atual) => navegar(visao, atual, -1));
              }}
              className="grid h-9 w-9 place-items-center rounded-md border border-borda bg-superficie font-texto text-body text-texto hover:border-texto-tenue/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
            >
              <span aria-hidden="true">‹</span>
            </button>
            <button
              type="button"
              aria-label="Próximo período"
              onClick={() => {
                setReferencia((atual) => navegar(visao, atual, 1));
              }}
              className="grid h-9 w-9 place-items-center rounded-md border border-borda bg-superficie font-texto text-body text-texto hover:border-texto-tenue/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
            >
              <span aria-hidden="true">›</span>
            </button>
          </div>

          <Botao
            variante="secundario"
            tamanho="sm"
            onClick={() => {
              setReferencia(inicioDoDia(new Date()));
            }}
          >
            Hoje
          </Botao>

          <div role="group" aria-label="Visão do calendário" className="flex items-center gap-1">
            {(["mes", "semana", "dia"] as const).map((opcao) => (
              <Botao
                key={opcao}
                tamanho="sm"
                variante={visao === opcao ? "primario" : "fantasma"}
                aria-pressed={visao === opcao}
                onClick={() => {
                  setVisao(opcao);
                }}
              >
                {opcao === "mes" ? "Mês" : opcao === "semana" ? "Semana" : "Dia"}
              </Botao>
            ))}
          </div>

          <Botao variante="fantasma" tamanho="sm" onClick={aoVoltar}>
            Voltar ao funil
          </Botao>
        </div>
      </header>

      {consulta.isPending && (
        <p role="status" className="font-texto text-body text-texto-suave">
          Carregando a agenda…
        </p>
      )}

      {consulta.isError && (
        <p role="alert" className="font-texto text-body text-carmim">
          Não foi possível carregar a agenda. Tente de novo em instantes.
        </p>
      )}

      {consulta.data !== undefined && consulta.data.meta.count === 0 && (
        /* Vazio convida, como a coluna vazia do funil: diz o que fazer, e não
           o que não existe. */
        <p className="rounded-lg border border-dashed border-borda px-4 py-6 text-center font-texto text-caption leading-relaxed text-texto-tenue">
          Nada marcado neste período. Abra uma oportunidade no funil e combine o próximo contato —
          ele aparece aqui.
        </p>
      )}

      {/* O alvo do guia é a moldura, e não a grade de dentro: as três visões
          trocam o conteúdo, e um alvo por visão daria três chances de o balão
          apontar para o que não está na tela. */}
      <div data-guia="agenda-grade" className="flex flex-col gap-6">
        {consulta.data !== undefined && visao === "mes" && (
          <GradeDoMes
            referencia={referencia}
            hoje={hoje}
            porDia={porDia}
            aoAbrir={abrir}
            aoVerODia={verODia}
          />
        )}

        {consulta.data !== undefined && visao !== "mes" && (
          <Lista
            dias={visao === "semana" ? diasDaSemana(referencia) : [inicioDoDia(referencia)]}
            hoje={hoje}
            porDia={porDia}
            aoAbrir={abrir}
          />
        )}
      </div>

      {aberta !== null && (
        <LimiteDeErro
          area="A ficha da oportunidade"
          acaoSecundaria={{
            rotulo: "Fechar",
            aoAcionar: () => {
              setAberta(null);
            },
          }}
        >
          <Suspense
            fallback={
              <p role="status" className="p-6 font-texto text-body text-texto-suave">
                Abrindo o negócio…
              </p>
            }
          >
            <PainelDaOportunidade
              opportunityId={aberta.id}
              tituloProvisorio={aberta.titulo}
              aoFechar={() => {
                setAberta(null);
              }}
            />
          </Suspense>
        </LimiteDeErro>
      )}
    </section>
  );
}

function GradeDoMes({
  referencia,
  hoje,
  porDia,
  aoAbrir,
  aoVerODia,
}: {
  referencia: Date;
  hoje: Date;
  porDia: Map<string, Compromisso[]>;
  aoAbrir: (compromisso: Compromisso) => void;
  aoVerODia: (data: Date) => void;
}) {
  const semanas = gradeDoMes(referencia);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] border-collapse">
        <caption className="sr-only">Compromissos de {rotuloDoPeriodo("mes", referencia)}</caption>
        <thead>
          <tr>
            {DIAS_DA_SEMANA.map((dia) => (
              <th
                key={dia}
                scope="col"
                className="border border-borda bg-superficie px-2 py-2 font-texto text-micro font-semibold uppercase tracking-wide text-texto-suave"
              >
                {dia}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {semanas.map((semana) => (
            <tr key={chaveDoDia(semana[0] as Date)}>
              {semana.map((data) => {
                const itens = porDia.get(chaveDoDia(data)) ?? [];
                const doMes = data.getMonth() === referencia.getMonth();
                const ehHoje = mesmoDia(data, hoje);

                return (
                  <td
                    key={chaveDoDia(data)}
                    className={[
                      "h-[132px] w-[14.28%] border border-borda align-top",
                      doMes ? "bg-superficie" : "bg-canvas",
                      ehHoje ? "outline outline-2 -outline-offset-2 outline-acao" : "",
                    ].join(" ")}
                  >
                    <div className="flex h-full flex-col gap-1 p-1.5">
                      <p
                        className={[
                          "text-right font-dados text-micro tabular-nums",
                          doMes ? "text-texto-suave" : "text-texto-tenue",
                          ehHoje ? "font-bold text-acao" : "",
                        ].join(" ")}
                      >
                        {/* "Hoje" escrito, e não só o contorno verde: o
                            contorno sozinho não é lido por quem usa leitor de
                            tela nem por quem não distingue a cor. */}
                        {ehHoje && <span className="mr-1 font-texto">hoje</span>}
                        {data.getDate()}
                      </p>

                      {itens.slice(0, LINHAS_POR_DIA).map((item) => (
                        <LinhaDoDia key={item.opportunity_id} item={item} aoAbrir={aoAbrir} />
                      ))}

                      {itens.length > LINHAS_POR_DIA && (
                        <button
                          type="button"
                          onClick={() => {
                            aoVerODia(data);
                          }}
                          className="text-left font-texto text-micro text-texto-suave underline hover:text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
                        >
                          +{itens.length - LINHAS_POR_DIA} mais
                        </button>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Semana e dia: a mesma lista, com um dia ou sete. Uma grade de semana com
 * faixas de hora prometeria precisão que o compromisso não tem — ele é um
 * horário, não um intervalo. */
function Lista({
  dias,
  hoje,
  porDia,
  aoAbrir,
}: {
  dias: Date[];
  hoje: Date;
  porDia: Map<string, Compromisso[]>;
  aoAbrir: (compromisso: Compromisso) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      {dias.map((data) => {
        const itens = porDia.get(chaveDoDia(data)) ?? [];
        const ehHoje = mesmoDia(data, hoje);

        return (
          <section
            key={chaveDoDia(data)}
            aria-label={new Intl.DateTimeFormat("pt-BR", {
              weekday: "long",
              day: "numeric",
              month: "long",
            }).format(data)}
            className="flex flex-col gap-2"
          >
            <h3
              className={[
                "font-texto text-caption font-semibold uppercase tracking-wide",
                ehHoje ? "text-acao" : "text-texto-suave",
              ].join(" ")}
            >
              {new Intl.DateTimeFormat("pt-BR", {
                weekday: "long",
                day: "2-digit",
                month: "2-digit",
              }).format(data)}
              {ehHoje && " · hoje"}
            </h3>

            {itens.length === 0 ? (
              <p className="font-texto text-caption text-texto-tenue">Nada marcado.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {itens.map((item) => (
                  <li key={item.opportunity_id}>
                    <LinhaDoDia item={item} aoAbrir={aoAbrir} detalhada />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function LinhaDoDia({
  item,
  aoAbrir,
  detalhada = false,
}: {
  item: Compromisso;
  aoAbrir: (compromisso: Compromisso) => void;
  detalhada?: boolean;
}) {
  const empresa = item.company_name === null || item.company_name === "" ? null : item.company_name;
  // Primeiro nome: numa célula de calendário a largura é o recurso escasso, e
  // "Ana" identifica tão bem quanto "Ana Lima" dentro de um time. O nome
  // completo continua no rótulo acessível e na ficha.
  const responsavel =
    item.owner_name === null || item.owner_name === ""
      ? null
      : item.owner_name.trim().split(" ")[0];

  return (
    <button
      type="button"
      onClick={() => {
        aoAbrir(item);
      }}
      // O rótulo carrega o que a cor diz, para quem não a vê: o horário, o
      // cliente e o atraso.
      // O rótulo carrega o que a cor e a abreviação não dizem: horário,
      // negócio, responsável por extenso e o atraso.
      aria-label={
        `${horaDoCompromisso(item.due_at)} — ${item.title}` +
        (item.is_overdue ? " (atrasado)" : "") +
        (item.owner_name === null ? "" : `, com ${item.owner_name}`) +
        (item.subject === null ? "" : `: ${item.subject}`)
      }
      className={[
        "w-full truncate rounded-md px-2 py-1 text-left font-texto text-micro",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva",
        item.is_overdue
          ? "bg-ambar/15 text-ambar hover:bg-ambar/25"
          : "bg-acao/10 text-acao hover:bg-acao/20",
        detalhada ? "text-caption" : "",
      ].join(" ")}
    >
      <span className="font-dados font-semibold tabular-nums">
        {horaDoCompromisso(item.due_at)}
      </span>{" "}
      {item.is_overdue && <span className="font-semibold">atrasado · </span>}
      {item.title}
      {/* O responsável entra já na visão de mês, junto do horário e do cliente:
          numa agenda de time, "quem vai falar" é tão decisivo quanto "quando" —
          sem isso, olhar o mês não diz de quem é o dia. */}
      {responsavel !== null && <span className="text-texto-suave"> · {responsavel}</span>}
      {detalhada && empresa !== null && <span className="text-texto-suave"> · {empresa}</span>}
      {detalhada && item.subject !== null && (
        <span className="text-texto-suave"> · {item.subject}</span>
      )}
      {detalhada && (
        <span className="text-texto-suave">
          {" "}
          · {formatarDinheiro(item.value_cents, item.currency)}
        </span>
      )}
    </button>
  );
}
